/* ============================================================================
 * 1. GLOBAL UTILITIES & HELPERS
 * ============================================================================ */

// Smooth Scroll Polyfill
if (!('scrollBehavior' in document.documentElement.style)) {
  HTMLElement.prototype.scrollIntoView = (function(original) {
    return function(options) {
      if (typeof options === 'object' && options.behavior === 'smooth') {
        const target = this;
        const targetY = target.getBoundingClientRect().top + window.pageYOffset;
        const startY = window.pageYOffset;
        const distance = targetY - startY;
        const duration = 300;
        let startTime = null;

        function easeInOutQuad(t) {
          return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        }

        function animation(currentTime) {
          if (startTime === null) startTime = currentTime;
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          window.scrollTo(0, startY + distance * easeInOutQuad(progress));
          
          if (progress < 1) {
            requestAnimationFrame(animation);
          }
        }

        requestAnimationFrame(animation);
      } else {
        original.call(this, options);
      }
    };
  })(HTMLElement.prototype.scrollIntoView);
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

const serializeForm = (form) => {
  try {
    let obj = {};
    let formData = new FormData(form);
    for (let key of formData.keys()) {
      obj[key] = formData.get(key);
    }
    return JSON.stringify(obj);
  } catch (error) {
    console.error('Error serializing form:', error);
    return '{}';
  }
};

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/" + type,
    },
  };
}

function defaultFormat(cents, decimals = 2, thousands = ",", decimal = ".") {
  if (isNaN(cents) || cents == null) return "0";
  let parts = (cents / 100).toFixed(decimals).split(".");
  let numberPart = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1" + thousands);
  let decimalPart = decimals > 0 ? decimal + parts[1] : "";
  return numberPart + decimalPart;
}

function formatMoney(cents, format = "") {
  if (typeof cents === "string") cents = cents.replace(".", "");
  let value = "";
  let placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  let formatString = format || theme.moneyFormat;

  let match = formatString.match(placeholderRegex);
  let formatType = match ? match[1] : "amount";

  switch (formatType) {
    case "amount":
      value = defaultFormat(cents, 2, ",", ".");
      break;
    case "amount_no_decimals":
      value = defaultFormat(cents, 0, ",", ".");
      break;
    case "amount_with_comma_separator":
      value = defaultFormat(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = defaultFormat(cents, 0, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = defaultFormat(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_space_separator":
      value = defaultFormat(cents, 0, " ", ".");
      break;
    case "amount_with_space_separator":
      value = defaultFormat(cents, 2, " ", ",");
      break;
    case "amount_with_period_and_space_separator":
      value = defaultFormat(cents, 2, " ", ".");
      break;
    default:
      value = defaultFormat(cents, 2, ",", ".");
  }
  return formatString.replace(placeholderRegex, value);
}

/* ============================================================================
 * 2. ACCESSIBILITY & MEDIA CONTROLS
 * ============================================================================ */

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() === "ESCAPE") {
    let detailsElement = event.target.closest("details[open]");
    if (detailsElement) {
      let summaryElement = detailsElement.querySelector("summary");
      detailsElement.removeAttribute("open");
      summaryElement.setAttribute("aria-expanded", false);
      summaryElement.focus();
    }
  }
}

if (navigator.userAgent.indexOf("iPhone") > -1) {
  document.querySelector("[name=viewport]").setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1");
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", "false");
  
  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }
  
  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });
  
  if (!summary.closest("header-drawer")) {
    summary.parentElement.addEventListener("keyup", onKeyUpEscape);
  }
});

let trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  let focusableElements = getFocusableElements(container);
  let firstElement = focusableElements[0];
  let lastElement = focusableElements[focusableElements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== lastElement && event.target !== firstElement) {
      document.addEventListener("keydown", trapFocusHandlers.keydown);
    }
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() === "TAB") {
      if (event.target === lastElement && !event.shiftKey) {
        event.preventDefault();
        firstElement.focus();
      }
      if ((event.target === container || event.target === firstElement) && event.shiftKey) {
        event.preventDefault();
        lastElement.focus();
      }
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  if (elementToFocus) elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus && !elementToFocus.classList.contains("card-focused")) {
    elementToFocus.focus();
  }
}

window.pauseAllMedia = async function (activeMedia) {
  let videos = document.querySelectorAll("deferred-media video");
  let iframes = document.querySelectorAll("deferred-media iframe");
  let models = document.querySelectorAll("deferred-media model-viewer");
  let promises = [];

  videos.forEach((video) => {
    if (video !== activeMedia) {
      let pausePromise = video.pause();
      if (pausePromise !== undefined) promises.push(pausePromise);
    }
  });

  iframes.forEach((iframe) => {
    let src = iframe.src;
    if (iframe !== activeMedia) {
      if (src && src.includes("youtube.com")) {
        iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
      } else if (src && src.includes("vimeo.com")) {
        let pausePromise = new Vimeo.Player(iframe).pause();
        if (pausePromise !== undefined) promises.push(pausePromise);
      }
    }
  });

  models.forEach((model) => {
    if (model !== activeMedia) {
      if (model.modelViewerUI && typeof model.modelViewerUI.pause === "function") {
        let pausePromise = model.modelViewerUI.pause();
        if (pausePromise !== undefined) promises.push(pausePromise);
      } else {
        console.error("modelViewerUI or its pause function is not available.");
      }
    }
  });

  return Promise.all(promises);
};

/* ============================================================================
 * Product media gallery (color-filtered images, images-ready)
 * ============================================================================ */

const initProductMediaGallery = (productSection) => {
  if (!productSection) return;

  const getNormalisedColor = (alt) => {
    if (!alt) return "";
    return alt.split("_")[0].trim().toLowerCase();
  };

  if (productSection.__galleryCurrentColor === undefined) {
    productSection.__galleryCurrentColor = null;
  }

  const updateImages = (selectedColor, resetSlide) => {
    const normalizeSelected = selectedColor ? selectedColor.toLowerCase() : null;
    const mediaItems = productSection.querySelectorAll(
      ".product__media-item:not(.swiper-slide-duplicate)"
    );

    mediaItems.forEach((item) => {
      const img = item.querySelector("img");
      const fullAlt = img?.alt || "";
      const altColor = getNormalisedColor(fullAlt);

      let shouldShow = false;
      if (normalizeSelected) {
        if (altColor === "" || altColor === normalizeSelected) {
          shouldShow = true;
        }
      } else if (fullAlt.includes("_01")) {
        shouldShow = true;
      }

      if (shouldShow) {
        item.classList.remove("hidden-slide");
        item.style.display = "block";
      } else {
        item.classList.add("hidden-slide");
        item.style.display = "none";
      }
    });

    if (normalizeSelected) {
      const lSlides = Array.from(mediaItems).filter((item) => {
        const img = item.querySelector("img");
        const altText = (img?.alt || "").toUpperCase();
        const srcText = (img?.src || "").toUpperCase();
        return (altText.includes("_L") || srcText.includes("_L")) && item.style.display !== "none";
      });

      lSlides.forEach((item) => {
        if (item.parentNode) item.parentNode.appendChild(item);
      });
    }

    const swiperContainer = productSection.querySelector(".swiper");
    if (swiperContainer && swiperContainer.swiper) {
      swiperContainer.swiper.update();
      if (resetSlide) {
        swiperContainer.swiper.slideTo(0);
      }
    }
  };

  if (!productSection.__galleryChangeBound) {
    productSection.addEventListener("change", (e) => {
      const name = (e.target.name || "").toLowerCase();
      if (name.includes("color") || name.includes("colour")) {
        productSection.__galleryCurrentColor = e.target.value
          ? e.target.value.toLowerCase()
          : null;
        updateImages(e.target.value, true);
      }
    });
    productSection.__galleryChangeBound = true;
  }

  if (productSection.__galleryObserver) {
    productSection.__galleryObserver.disconnect();
  }

  productSection.__galleryObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type !== "attributes" ||
        mutation.attributeName !== "class" ||
        !mutation.target.classList.contains("active")
      ) {
        return;
      }

      const target = mutation.target;
      if (!productSection.__galleryCurrentColor) return;

      const img = target.querySelector("img");
      const alt = img ? img.alt : "";
      const slideColor = getNormalisedColor(alt);

      if (slideColor !== "" && slideColor !== productSection.__galleryCurrentColor) {
        target.classList.remove("active");
      }
    });
  });

  productSection
    .querySelectorAll(".product__media-item:not(.swiper-slide-duplicate)")
    .forEach((item) => {
      productSection.__galleryObserver.observe(item, {
        attributes: true,
        attributeFilter: ["class"],
      });
    });

  const defaultInput =
    productSection.querySelector(
      'input[name*="Color"]:checked, input[name*="Colour"]:checked'
    ) ||
    productSection.querySelector(
      'select[name*="Color"] option:checked, select[name*="Colour"] option:checked'
    );

  setTimeout(() => {
    if (defaultInput && defaultInput.value) {
      productSection.__galleryCurrentColor = defaultInput.value.toLowerCase();
      updateImages(defaultInput.value, true);
    } else {
      updateImages(null, true);
    }
    productSection.classList.add("images-ready");

    let hasVisibleMedia = false;
    productSection
      .querySelectorAll(".product__media-item:not(.swiper-slide-duplicate)")
      .forEach((item) => {
        if (item.style.display !== "none" && !item.classList.contains("hidden-slide")) {
          hasVisibleMedia = true;
        }
      });

    if (!hasVisibleMedia) {
      productSection
        .querySelectorAll(".product__media-item:not(.swiper-slide-duplicate)")
        .forEach((item) => {
          item.classList.remove("hidden-slide");
          item.style.display = "block";
        });
    }
  }, 100);

  productSection.dataset.mediaGalleryInit = "true";
};

const getQuickAddProductSection = (modalOrContainer) => {
  const root = modalOrContainer?.modalContent || modalOrContainer;
  if (!root) return null;
  return (
    root.querySelector('section[id^="MainProduct-"]') ||
    root.querySelector("section.main-product") ||
    root.querySelector(".main-product")
  );
};

window.initProductMediaGallery = initProductMediaGallery;
window.getQuickAddProductSection = getQuickAddProductSection;

/* ============================================================================
 * 3. SLIDERS & CAROUSELS (Swiper.js)
 * ============================================================================ */

const sliderInit = (updateTimeout) => {
  const mediaLists = document.querySelectorAll(".js-media-list");
  if (!mediaLists || mediaLists.length === 0) return;

  mediaLists.forEach((mediaList, index) => {
    let swiperInstance;

    const commonSwiperConfig = {
      slidesPerView: 1,
      spaceBetween: 1,
      mousewheel: { forceToAxis: true },
      autoHeight: false,
      speed: 500,
      navigation: {
        nextEl: ".product .product__slider-nav .swiper-button-next",
        prevEl: ".product .product__slider-nav .swiper-button-prev",
      },
      pagination: {
        el: ".product .product__pagination",
        type: "custom",
        renderCustom: function (swiper, current, total) {
          let html = "";
          for (let i = 1; i < total + 1; i++) {
            if (i === current) {
              html += `<span class="swiper-pagination-bullet swiper-pagination-bullet-active" tabindex="0" role="button" aria-label="Go to slide ${i}"></span>`;
            } else {
              html += `<span class="swiper-pagination-bullet" tabindex="0" role="button" aria-label="Go to slide ${i}"></span>`;
            }
          }
          return html;
        },
      },
      on: {
        slideChange: function () {
          window.pauseAllMedia();
          this.params.noSwiping = false;
          let popupSlider = document.querySelector(".js-popup-slider");
          if (popupSlider && popupSlider.swiper) {
            popupSlider.swiper.slideTo(this.activeIndex);
          }
        },
        slideChangeTransitionEnd: function () {
          let modelViewer = this.slides[this.activeIndex].querySelector("model-viewer");
          if (modelViewer) {
            this.slides[this.activeIndex].querySelector(".shopify-model-viewer-ui__button--poster").removeAttribute("hidden");
          }
        },
        touchStart: function () {
          let modelViewer = this.slides[this.activeIndex].querySelector("model-viewer");
          if (modelViewer) {
            if (modelViewer.classList.contains("shopify-model-viewer-ui__disabled")) {
              this.params.noSwiping = false;
            } else {
              this.params.noSwiping = true;
              this.params.noSwipingClass = "swiper-slide";
            }
          }
        },
      },
    };

    if (mediaList.classList.contains("js-media-list-mobile")) {
      new ResizeObserver((entries) => {
        entries.forEach(() => {
          if (document.body.clientWidth > 575) {
            if (swiperInstance) swiperInstance.destroy();
            mediaList.querySelector(".swiper-wrapper").removeAttribute("style");
            mediaList.classList.remove("swiper");
          } else {
            if (!mediaList.classList.contains("swiper-initialized")) {
              swiperInstance = new Swiper(mediaList, commonSwiperConfig);
            }
          }
        });
      }).observe(mediaList.closest(".product__outer"));

    } else if (mediaList.classList.contains("stacked-media-list-mobile")) {
      new ResizeObserver((entries) => {
        entries.forEach(() => {
          if (document.body.clientWidth <= 575) {
            if (swiperInstance) swiperInstance.destroy();
            mediaList.querySelector(".swiper-wrapper").removeAttribute("style");
            mediaList.classList.remove("swiper");
          } else {
            if (!mediaList.classList.contains("swiper-initialized")) {
              let sublists = document.querySelectorAll(".js-media-sublist");
              let thumbsConfig = Object.assign({}, commonSwiperConfig, {
                thumbs: {
                  swiper: sublists.length > 0 ? sublists[index]?.swiper : "",
                },
                on: {
                  ...commonSwiperConfig.on,
                  slideChangeTransitionStart: function () {
                    let sublist = document.querySelector(".js-media-sublist");
                    if (sublist) {
                      sublist.swiper.slideTo(document.querySelector(".js-media-list").swiper.activeIndex);
                    }
                  },
                },
              });
              swiperInstance = new Swiper(mediaList, thumbsConfig);
              if (updateTimeout) setTimeout(() => swiperInstance.update(), 800);
            }
          }
        });
      }).observe(mediaList.closest(".product__outer"));

    } else {
      if (!mediaList.classList.contains("swiper-initialized")) {
        let sublists = document.querySelectorAll(".js-media-sublist");
        let thumbsConfig = Object.assign({}, commonSwiperConfig, {
          thumbs: {
            swiper: sublists.length > 0 ? sublists[index]?.swiper : "",
          },
          on: {
            ...commonSwiperConfig.on,
            slideChangeTransitionStart: function () {
              let sublist = document.querySelector(".js-media-sublist");
              if (sublist) {
                sublist.swiper.slideTo(document.querySelector(".js-media-list").swiper.activeIndex);
              }
            },
          },
        });
        swiperInstance = new Swiper(mediaList, thumbsConfig);
        if (updateTimeout) setTimeout(() => swiperInstance.update(), 800);
      }
    }
  });
};

const subSliderInit = (updateTimeout) => {
  const subLists = document.querySelectorAll(".js-media-sublist");
  if (subLists && subLists.length > 0) {
    subLists.forEach((sublist) => {
      let direction = "vertical";
      let autoHeight = true;
      let slidesPerView = "auto";
      let slidesPerViewDesktop = "auto";

      if (sublist.classList.contains("js-media-sublist--horizontal")) {
        direction = "horizontal";
        autoHeight = false;
        slidesPerView = 7;
        slidesPerViewDesktop = 5;
      }

      let swiperInstance = new Swiper(sublist, {
        slidesPerView: slidesPerViewDesktop,
        spaceBetween: 4,
        direction: direction,
        autoHeight: autoHeight,
        freeMode: false,
        mousewheel: { forceToAxis: true },
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        breakpoints: {
          750: { slidesPerView: slidesPerView },
          990: { slidesPerView: slidesPerViewDesktop },
          1200: { slidesPerView: slidesPerView },
        },
        on: {
          touchEnd: function (event) {
            let diff = event.touches.diff = event.isHorizontal()
              ? event.touches.currentX - event.touches.startX
              : event.touches.currentY - event.touches.startY;
            if (diff < 5 || diff > -5) {
              event.allowClick = true;
            }
          },
        },
      });

      if (updateTimeout) setTimeout(() => swiperInstance.update(), 800);
    });
  }
};

const popupSliderInit = (updateTimeout) => {
  const popupSliderEl = document.querySelector(".js-popup-slider");
  if (popupSliderEl) {
    let swiperInstance = new Swiper(popupSliderEl, {
      slidesPerView: 1,
      mousewheel: { forceToAxis: true },
      navigation: {
        nextEl: ".product-media-modal .product__slider-nav .swiper-button-next",
        prevEl: ".product-media-modal .product__slider-nav .swiper-button-prev",
      },
      on: {
        afterInit: function () {
          if (document.querySelector(".product__outer--grid-gallery") || document.querySelector(".product__outer--stacked-gallery")) {
            document.querySelectorAll(".product__media-list .product__media-toggle").forEach((toggle, index) => {
              toggle.addEventListener("click", () => {
                let popupSlider = document.querySelector(".js-popup-slider");
                if (popupSlider && popupSlider.swiper) {
                  popupSlider.swiper.slideTo(index);
                }
              });
            });
          }
        },
        slideChange: function () {
          window.pauseAllMedia();
          this.params.noSwiping = false;
          document.querySelector(".product-media-modal__content").classList.remove("zoom");
        },
        touchMove: function () {
          document.querySelector(".product-media-modal__content").classList.remove("zoom");
        },
        slideChangeTransitionEnd: function () {
          let modelViewer = this.slides[this.activeIndex].querySelector("model-viewer");
          if (modelViewer) {
            this.slides[this.activeIndex].querySelector(".shopify-model-viewer-ui__button--poster").removeAttribute("hidden");
          }
        },
        touchStart: function () {
          let modelViewer = this.slides[this.activeIndex].querySelector("model-viewer");
          if (modelViewer) {
            if (modelViewer.classList.contains("shopify-model-viewer-ui__disabled")) {
              this.params.noSwiping = false;
            } else {
              this.params.noSwiping = true;
              this.params.noSwipingClass = "swiper-slide";
            }
          }
        },
      },
    });

    if (updateTimeout) setTimeout(() => swiperInstance.update(), 800);
  }
};

const sliderQuickAddInit = (container) => {
  let mediaList = container.querySelector(".product__media-list");
  if (mediaList) {
    if (!mediaList.classList.contains("swiper")) {
      mediaList.classList.add("swiper");
      let innerHTML = mediaList.innerHTML;
      mediaList.innerHTML = "<div class='swiper-wrapper'>" + innerHTML + "</div>";
    }

    let swiperInstance = new Swiper(container.querySelector(".product__media-list"), {
      slidesPerView: 2,
      slidesPerGroup: 1,
      mousewheel: { forceToAxis: true },
      spaceBetween: 8,
      speed: 500,
      navigation: {
        nextEl: container.querySelector(".product__slider-nav .swiper-button-next"),
        prevEl: container.querySelector(".product__slider-nav .swiper-button-prev"),
      },
      pagination: {
        el: container.querySelector(".product__pagination"),
        type: "custom",
        renderCustom: function (swiper, current, total) {
          let html = "";
          for (let i = 1; i < total + 1; i++) {
            if (i === current) {
              html += `<span class="swiper-pagination-bullet swiper-pagination-bullet-active" tabindex="0" role="button" aria-label="Go to slide ${i}"></span>`;
            } else {
              html += `<span class="swiper-pagination-bullet" tabindex="0" role="button" aria-label="Go to slide ${i}"></span>`;
            }
          }
          return html;
        },
      },
      breakpoints: {
        750: { slidesPerView: 1, slidesPerGroup: 1, spaceBetween: 0 },
      },
      on: {
        slideChangeTransitionStart: function () {
          let mediaListGlobal = document.querySelector(".product__media-list");
          if (mediaListGlobal && mediaListGlobal.swiper) {
            mediaListGlobal.swiper.slideTo(mediaListGlobal.swiper.activeIndex);
          }
        },
        slideChange: function () {
          window.pauseAllMedia();
          this.params.noSwiping = false;
        },
        slideChangeTransitionEnd: function () {
          let modelViewer = this.slides[this.activeIndex].querySelector("model-viewer");
          if (modelViewer) {
            this.slides[this.activeIndex].querySelector(".shopify-model-viewer-ui__button--poster").removeAttribute("hidden");
          }
        },
        touchStart: function () {
          let modelViewer = this.slides[this.activeIndex].querySelector("model-viewer");
          if (modelViewer) {
            if (modelViewer.classList.contains("shopify-model-viewer-ui__disabled")) {
              this.params.noSwiping = false;
            } else {
              this.params.noSwiping = true;
              this.params.noSwipingClass = "swiper-slide";
            }
          }
        },
      },
    });

    setTimeout(() => swiperInstance.update(), 800);
  }
};

const productSubSliderInitInContainer = (container, updateTimeout) => {
  const subLists = container.querySelectorAll(".js-media-sublist");
  if (!subLists || subLists.length === 0) return;

  subLists.forEach((sublist) => {
    if (sublist.classList.contains("swiper-initialized")) return;

    let direction = "vertical";
    let autoHeight = true;
    let slidesPerView = "auto";
    let slidesPerViewDesktop = "auto";

    if (sublist.classList.contains("js-media-sublist--horizontal")) {
      direction = "horizontal";
      autoHeight = false;
      slidesPerView = 7;
      slidesPerViewDesktop = 5;
    }

    const swiperInstance = new Swiper(sublist, {
      slidesPerView: slidesPerViewDesktop,
      spaceBetween: 4,
      direction: direction,
      autoHeight: autoHeight,
      freeMode: false,
      mousewheel: { forceToAxis: true },
      watchSlidesProgress: true,
      watchSlidesVisibility: true,
      breakpoints: {
        750: { slidesPerView: slidesPerView },
        990: { slidesPerView: slidesPerViewDesktop },
        1200: { slidesPerView: slidesPerView },
      },
      on: {
        touchEnd: function (event) {
          let diff = (event.touches.diff = event.isHorizontal()
            ? event.touches.currentX - event.touches.startX
            : event.touches.currentY - event.touches.startY);
          if (diff < 5 || diff > -5) {
            event.allowClick = true;
          }
        },
      },
    });

    if (updateTimeout) setTimeout(() => swiperInstance.update(), 800);
  });
};

const productMediaSlidersInitInContainer = (container, updateTimeout) => {
  productSubSliderInitInContainer(container, updateTimeout);

  const mediaLists = container.querySelectorAll(".js-media-list");
  if (mediaLists && mediaLists.length > 0) {
    mediaLists.forEach((mediaList, index) => {
      if (mediaList.classList.contains("swiper-initialized")) return;

      let swiperInstance;
      const sublists = container.querySelectorAll(".js-media-sublist");

      const commonSwiperConfig = {
        slidesPerView: 1,
        spaceBetween: 1,
        mousewheel: { forceToAxis: true },
        autoHeight: false,
        speed: 500,
        navigation: {
          nextEl: container.querySelector(".product__slider-nav .swiper-button-next"),
          prevEl: container.querySelector(".product__slider-nav .swiper-button-prev"),
        },
        pagination: {
          el: container.querySelector(".product__pagination"),
          type: "custom",
          renderCustom: function (swiper, current, total) {
            let html = "";
            for (let i = 1; i < total + 1; i++) {
              if (i === current) {
                html += `<span class="swiper-pagination-bullet swiper-pagination-bullet-active" tabindex="0" role="button" aria-label="Go to slide ${i}"></span>`;
              } else {
                html += `<span class="swiper-pagination-bullet" tabindex="0" role="button" aria-label="Go to slide ${i}"></span>`;
              }
            }
            return html;
          },
        },
        on: {
          slideChange: function () {
            window.pauseAllMedia();
            this.params.noSwiping = false;
          },
          slideChangeTransitionEnd: function () {
            let modelViewer = this.slides[this.activeIndex].querySelector("model-viewer");
            if (modelViewer) {
              this.slides[this.activeIndex]
                .querySelector(".shopify-model-viewer-ui__button--poster")
                .removeAttribute("hidden");
            }
          },
          touchStart: function () {
            let modelViewer = this.slides[this.activeIndex].querySelector("model-viewer");
            if (modelViewer) {
              if (modelViewer.classList.contains("shopify-model-viewer-ui__disabled")) {
                this.params.noSwiping = false;
              } else {
                this.params.noSwiping = true;
                this.params.noSwipingClass = "swiper-slide";
              }
            }
          },
        },
      };

      const initMainSwiper = () => {
        if (mediaList.classList.contains("swiper-initialized")) return;

        const thumbsConfig = Object.assign({}, commonSwiperConfig, {
          thumbs: {
            swiper: sublists.length > 0 ? sublists[index]?.swiper : "",
          },
          on: {
            ...commonSwiperConfig.on,
            slideChangeTransitionStart: function () {
              const sublist = sublists[index];
              const mainList = mediaList;
              if (sublist?.swiper && mainList?.swiper) {
                sublist.swiper.slideTo(mainList.swiper.activeIndex);
              }
            },
          },
        });

        swiperInstance = new Swiper(mediaList, thumbsConfig);
        if (updateTimeout) setTimeout(() => swiperInstance.update(), 800);
      };

      if (mediaList.classList.contains("js-media-list-mobile")) {
        new ResizeObserver(() => {
          if (document.body.clientWidth > 575) {
            if (swiperInstance) swiperInstance.destroy(true, true);
            const wrapper = mediaList.querySelector(".swiper-wrapper");
            if (wrapper) wrapper.removeAttribute("style");
            mediaList.classList.remove("swiper");
          } else if (!mediaList.classList.contains("swiper-initialized")) {
            swiperInstance = new Swiper(mediaList, commonSwiperConfig);
          }
        }).observe(mediaList.closest(".product__outer") || container);

      } else if (mediaList.classList.contains("stacked-media-list-mobile")) {
        new ResizeObserver(() => {
          if (document.body.clientWidth <= 575) {
            if (swiperInstance) swiperInstance.destroy(true, true);
            const wrapper = mediaList.querySelector(".swiper-wrapper");
            if (wrapper) wrapper.removeAttribute("style");
            mediaList.classList.remove("swiper");
          } else {
            initMainSwiper();
          }
        }).observe(mediaList.closest(".product__outer") || container);

      } else {
        initMainSwiper();
      }
    });
    return;
  }

  sliderQuickAddInit(container);
};

window.productMediaSlidersInitInContainer = productMediaSlidersInitInContainer;

/* ============================================================================
 * 4. SHOPIFY LEGACY POLFILLS & OBJECTS
 * ============================================================================ */

if (typeof window.Shopify === "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, context) {
  return function () {
    return fn.apply(context, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (let i = 0, len = selector.options.length; i < len; i++) {
    let option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (element, eventName, handler) {
  if (element.addEventListener) {
    element.addEventListener(eventName, handler, false);
  } else {
    element.attachEvent("on" + eventName, handler);
  }
};

Shopify.postLink = function (url, options) {
  options = options || {};
  let method = options.method || "post";
  let parameters = options.parameters || {};
  let form = document.createElement("form");
  
  form.setAttribute("method", method);
  form.setAttribute("action", url);
  
  for (let key in parameters) {
    let input = document.createElement("input");
    input.setAttribute("type", "hidden");
    input.setAttribute("name", key);
    input.setAttribute("value", parameters[key]);
    form.appendChild(input);
  }
  
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (countrySelectorId, provinceSelectorId, options) {
  this.countryEl = document.getElementById(countrySelectorId);
  this.provinceEl = document.getElementById(provinceSelectorId);
  this.provinceContainer = document.getElementById(options.hideElement || provinceSelectorId);
  Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));
  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    let defaultVal = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, defaultVal);
    this.countryHandler();
  },
  initProvince: function () {
    let defaultVal = this.provinceEl.getAttribute("data-default");
    if (defaultVal && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, defaultVal);
    }
  },
  countryHandler: function () {
    let opt = this.countryEl.options[this.countryEl.selectedIndex];
    let raw = opt.getAttribute("data-provinces");
    let provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (let i = 0; i < provinces.length; i++) {
        let option = document.createElement("option");
        option.value = provinces[i][0];
        option.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(option);
      }
      this.provinceContainer.style.display = "";
    }
  },
  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },
  setOptions: function (selector, values) {
    for (let i = 0; i < values.length; i++) {
      let option = document.createElement("option");
      option.value = values[i];
      option.innerHTML = values[i];
      selector.appendChild(option);
    }
  },
};

/* ============================================================================
 * 5. CUSTOM WEB COMPONENTS
 * ============================================================================ */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) => {
      this.setMinimumDisable();
      button.addEventListener("click", this.onButtonClick.bind(this));
    });

    ["paste", "input"].forEach((eventName) => {
      this.input.addEventListener(eventName, function (event) {
        if (!/^0*?[1-9]\d*$/.test(event.currentTarget.value) && event.currentTarget.value !== "") {
          event.currentTarget.value = 1;
        }
        
        if (event.currentTarget.value == 1 || event.currentTarget.value === "") {
          this.previousElementSibling.classList.add("disabled");
        } else {
          this.previousElementSibling.classList.remove("disabled");
        }
      });
    });

    this.input.addEventListener("focusout", function (event) {
      if (event.currentTarget.value === "") {
        event.currentTarget.value = 1;
      }
    });
  }

  setMinimumDisable() {
    if (this.input.value == 1) {
      this.querySelector('button[name="minus"]').classList.add("disabled");
    } else {
      this.querySelector('button[name="minus"]').classList.remove("disabled");
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    let previousValue = this.input.value;

    if (event.target.name === "plus") {
      this.input.stepUp();
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }
    this.setMinimumDisable();
  }
}
customElements.define("quantity-input", QuantityInput);


class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    let summaries = this.querySelectorAll("summary");
    this.addAccessibilityAttributes(summaries);

    if (navigator.platform === "iPhone") {
      document.documentElement.style.setProperty("--viewport-height", window.innerHeight + "px");
    }

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) => {
      summary.addEventListener("click", this.onSummaryClick.bind(this));
    });
    this.querySelectorAll("button").forEach((button) => {
      let ignores = [
        ".toggle-scheme-button",
        ".header__localization-button",
        ".header__localization-lang-button",
        ".mobile-localization-form__back--reg",
        ".mobile-localization-form__back--lang",
      ];
      if (!ignores.some((selector) => button.matches(selector))) {
        button.addEventListener("click", this.onCloseButtonClick.bind(this));
      }
    });
  }

  addAccessibilityAttributes(elements) {
    elements.forEach((element) => {
      element.setAttribute("role", "button");
      element.setAttribute("aria-expanded", false);
      element.setAttribute("aria-controls", element.nextElementSibling.id);
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() === "ESCAPE") {
      let openDetails = event.target.closest("details[open]");
      if (openDetails) {
        if (openDetails === this.mainDetailsToggle) {
          this.closeMenuDrawer(this.mainDetailsToggle.querySelector("summary"));
        } else {
          this.closeSubmenu(openDetails);
        }
      }
    }
  }

  onSummaryClick(event) {
    let summary = event.currentTarget;
    let details = summary.parentNode;
    let isOpen = details.hasAttribute("open");

    if (details === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(summary) : this.openMenuDrawer(summary);
    } else {
      trapFocus(summary.nextElementSibling, details.querySelector("button"));
      setTimeout(() => {
        details.classList.add("menu-opening");
      });
    }
  }

  openMenuDrawer(summary) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summary.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summary);
    document.body.classList.add("overflow-hidden-drawer");
  }

  closeMenuDrawer(summary, removeFocus = false) {
    if (summary !== undefined) {
      this.mainDetailsToggle.classList.remove("menu-opening");
      this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
        details.removeAttribute("open");
        details.classList.remove("menu-opening");
      });
      this.mainDetailsToggle.querySelector("summary").setAttribute("aria-expanded", false);
      document.body.classList.remove("overflow-hidden-drawer");
      removeTrapFocus(removeFocus);
      this.closeAnimation(this.mainDetailsToggle);
    }
  }

  onFocusOut(event) {
    if (event.relatedTarget && event.relatedTarget.classList.contains("mobile-localization-form__back")) {
      return;
    }
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) {
        this.closeMenuDrawer();
      }
    });
  }

  onCloseButtonClick(event) {
    let details = event.currentTarget.closest("details");
    this.closeSubmenu(details);
  }

  closeSubmenu(details) {
    details.classList.remove("menu-opening");
    removeTrapFocus();
    this.closeAnimation(details);
  }

  closeAnimation(details) {
    let startTime;
    const animate = (time) => {
      if (startTime === undefined) startTime = time;
      if (time - startTime < 400) {
        window.requestAnimationFrame(animate);
      } else {
        details.removeAttribute("open");
        if (!details.closest(".burger-menu") && details.closest("details[open]")) {
          trapFocus(details.closest("details[open]"), details.querySelector("summary"));
        }
      }
    };
    window.requestAnimationFrame(animate);
  }
}
customElements.define("menu-drawer", MenuDrawer);


class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summary) {
    this.headerWrapper = document.querySelector(".header-wrapper");
    document.documentElement.style.setProperty("--header-bottom-position", parseInt(this.headerWrapper.getBoundingClientRect().top) + "px");
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summary.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summary);
    document.body.classList.add("overflow-hidden-drawer");
  }
}
customElements.define("header-drawer", HeaderDrawer);


class ModalDialog extends HTMLElement {
  constructor() {
    super();
    let closeBtn = this.querySelector('[id^="ModalClose-"]');
    if (closeBtn) {
      closeBtn.addEventListener("click", () => this.hide(false));
    }
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });

    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (event.pointerType === "mouse" && !event.target.closest("deferred-media, product-model")) {
          this.hide();
        }
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (!this.moved) {
      this.moved = true;
      document.body.appendChild(this);
    }
  }

  show(opener) {
    this.openedBy = opener;
    let templatePopup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden-modal");
    this.setAttribute("open", "");
    
    if (templatePopup) templatePopup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide(focusOpener = true) {
    this.removeAttribute("open");
    removeTrapFocus(focusOpener ? this.openedBy : null);
    window.pauseAllMedia();
    
    if (!document.querySelector("body > quick-add-modal[open]")) {
      document.body.classList.remove("overflow-hidden-modal");
      document.body.dispatchEvent(new CustomEvent("modalClosed"));
    }
    
    let content = document.querySelector(".product-media-modal__content");
    if (content) content.classList.remove("zoom");
  }
}
customElements.define("modal-dialog", ModalDialog);


class ModalOpener extends HTMLElement {
  constructor() {
    super();
    let button = this.querySelector("button");
    if (button) {
      button.addEventListener("click", () => {
        let modal = document.querySelector(this.getAttribute("data-modal"));
        if (modal) modal.show(button);
      });
    }
  }
}
customElements.define("modal-opener", ModalOpener);


class ModalOpenerQuickView extends HTMLElement {
  constructor() {
    super();
    let button = this.querySelector("button");
    if (button) {
      button.addEventListener("click", () => {
        let modal = document.createElement("quick-add-modal");
        modal.id = button.dataset.modalId;
        modal.className = button.dataset.modalClass;
        modal.innerHTML = `
          <div role="dialog" aria-label="Choose options for ${button.dataset.modalAriaLabel}" aria-modal="true" class="quick-add-modal__content global-settings-popup js-mobile-swipe js-mobile-swipe-down">
            <div class="quick-add-modal__control js-mobile-swipe-control">
              <svg width="38" height="3" viewBox="0 0 38 3" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon icon-mobile-swipe"><rect width="38" height="3" rx="1.5" fill="currentColor"/></svg>
            </div>
            <button id="ModalClose-${button.dataset.productId}" type="button" class="quick-add-modal__toggle modal-close-button" aria-label="Close" tabindex="-1">
              <svg class="icon icon-close" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.99989 9.31163L2.29175 15.0198L0.980116 13.7081L6.68826 8L0.980116 2.29186L2.29175 0.980222L7.99989 6.68837L13.708 0.980222L15.0197 2.29186L9.31153 8L15.0197 13.7081L13.708 15.0198L7.99989 9.31163Z" fill="currentColor"/></svg>
            </button>
            <div class="quick-add-modal__content-wrapper">
              <div id="QuickAddInfo-${button.dataset.productId}" class="quick-add-modal__content-info"></div>
            </div>
          </div>
        `;
        modal.moved = true;
        modal.modalContent = modal.querySelector('[id^="QuickAddInfo-"]');
        modal.closeButton = modal.querySelector('[id^="ModalClose-"]');
        if (modal.closeButton) modal.closeButton.addEventListener("click", () => modal.hide(false));

        new SwipeMobile(modal.querySelector(".js-mobile-swipe"), "down", 160, 400, () => modal.hide(true));
        document.body.append(modal);
        modal.show(button);
      });
    }
  }
}
customElements.define("modal-opener-quick-view", ModalOpenerQuickView);


class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    let poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (poster) {
      poster.addEventListener("click", () => this.loadContent(true));
    }
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      let template = this.querySelector("template");
      if (template && template.content && template.content.firstElementChild) {
        let wrapper = document.createElement("div");
        wrapper.appendChild(template.content.firstElementChild.cloneNode(true));
        this.setAttribute("loaded", true);
        
        let mediaEl = this.appendChild(wrapper.firstElementChild);
        if (mediaEl instanceof HTMLVideoElement) {
          mediaEl.addEventListener("play", () => window.pauseAllMedia(mediaEl));
        } else if (mediaEl instanceof HTMLIFrameElement) {
          let src = mediaEl.src;
          if (src && src.includes("youtube.com")) {
            mediaEl.addEventListener("load", () => {
              mediaEl.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', "*");
              mediaEl.parentElement.addEventListener("click", () => window.pauseAllMedia(mediaEl));
            });
          } else if (src && src.includes("vimeo.com")) {
            mediaEl.addEventListener("load", () => {
              new Vimeo.Player(mediaEl).on("play", () => window.pauseAllMedia(mediaEl));
              mediaEl.parentElement.addEventListener("click", () => window.pauseAllMedia(mediaEl));
            });
          }
        } else if (mediaEl.tagName.toLowerCase() === "model-viewer") {
          mediaEl.addEventListener("load", () => {
            window.pauseAllMedia(mediaEl);
          });
        } else {
          console.error("Unsupported media element or HTMLModelViewerElement is null");
        }

        if (focus) {
          mediaEl.focus();
          if (mediaEl instanceof HTMLVideoElement && mediaEl.autoplay) {
            mediaEl.play().catch(console.error);
          }
        }
      } else {
        console.error("Template or its content is missing.");
      }
    }
  }
}
customElements.define("deferred-media", DeferredMedia);


class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", (e) => this.onVariantChange(e));
  }

  onVariantChange(event) {
    this.updateOptions();
    this.updateColorNameLabel();
    this.updateMasterId();
    this.toggleAddButton(true, "", false);
    this.updatePickupAvailability();
    this.updateVariantStatuses();

    // Only slide the gallery when the user changed a COLOR option.
    // Size (or any other option) must never move the gallery position.
    const changedName = (event?.target?.name || "").toLowerCase();
    const isColorChange = changedName.includes("color") || changedName.includes("colour");

    if (this.currentVariant) {
      if (this.currentVariant?.featured_media && this.dataset?.variantMediaDisplay === "show_all") {
        let isQuickAddOpen = Boolean(document.querySelector("body > quick-add-modal[open]"));
        let isStackedGallery = this.dataset.galleryType === "stacked_gallery";
        let mediaId = (isQuickAddOpen && isStackedGallery ? this.currentVariant : this.currentVariant.featured_media).id;
        this.updateMedia(this.dataset.section + "-" + mediaId, isColorChange);
      }
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
    } else {
      this.toggleAddButton(true, "", true);
      this.setUnavailable();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll("select"), (select) => select.value);
  }

  getSelectedColorValue() {
    let checkedColorInput = this.querySelector(
      'input[type="radio"][name*="Color"]:checked, input[type="radio"][name*="Colour"]:checked'
    );
    if (checkedColorInput) return checkedColorInput.value;

    let selectedColorOption = this.querySelector(
      'select[name*="Color"], select[name*="Colour"]'
    );
    if (selectedColorOption) return selectedColorOption.value;

    let colorFieldset = Array.from(this.querySelectorAll("fieldset")).find((fieldset) => {
      let legend = fieldset.querySelector("legend");
      let legendText = legend ? legend.textContent.toLowerCase() : "";
      return legendText.includes("color") || legendText.includes("colour");
    });
    if (!colorFieldset) return null;

    let fallbackChecked = colorFieldset.querySelector('input[type="radio"]:checked');
    return fallbackChecked ? fallbackChecked.value : null;
  }

  updateColorNameLabel() {
    let colorValue = this.getSelectedColorValue();
    if (!colorValue) return;

    this.querySelectorAll("[data-color-name-id]").forEach((node) => {
      node.textContent = colorValue;
    });
  }

  updateMasterId() {
    if (this.variantData || this.querySelector('[type="application/json"]')) {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => this.options[index] === option).includes(false);
      });
    }
  }

  isHidden(el) {
    let style = window.getComputedStyle(el);
    return style.display === "none" || style.visibility === "hidden";
  }

  updateMedia(mediaId, isColorChange = true) {
    if (this.currentVariant && this.currentVariant?.featured_media) {
      let sectionEl = document.querySelector(`[data-section="main-${this.dataset.section}"]`);
      this.mediaList = sectionEl.querySelector(".product__media-list");
      let targetMedia = this.mediaList.querySelector(`[data-media-id="${mediaId}"]`);
      
      // Only update .active (which drives CSS order:-1 in stacked gallery)
      // when the COLOR changed. A size change must never reorder the gallery.
      if (isColorChange) {
        this.mediaList.querySelectorAll("[data-media-id]").forEach((el) => {
          el.classList.remove("active");
        });
        if (targetMedia) targetMedia.classList.add("active");
      }

      // Only slide to the variant's featured media when the COLOR changed.
      // Changing size must not move the gallery — the color-based alt-text
      // logic in the liquid <script> owns the gallery position for size changes.
      if (isColorChange) {
        document.querySelectorAll(".product__media-wrapper").forEach((wrapper) => {
          if (!this.isHidden(wrapper)) {
            let wrapperTarget = wrapper.querySelector(`[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`);
            if (wrapper.querySelector(".product__media-list") && wrapper.querySelector(".product__media-list").swiper) {
              let index = wrapper.querySelector(".product__media-list").swiper.slides.indexOf(wrapperTarget);
              wrapper.querySelector(".product__media-list").swiper.slideTo(index);
            }
          }
        });
      }
    }
  }

  updateURL() {
    if (!this.classList.contains("featured-product-radios") && this.currentVariant && this.dataset.updateUrl !== "false") {
      window.history.replaceState({}, "", this.dataset.url + "?variant=" + this.currentVariant.id);
    }
  }

  updateVariantInput() {
    let selectors = this.dataset.index 
      ? `#product-form-${this.dataset.section}-${this.dataset.index}, #product-form-installment-${this.dataset.section}-${this.dataset.index}` 
      : `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`;
      
    document.querySelectorAll(selectors).forEach((form) => {
      let input = form.querySelector('input[name="id"]');
      if(input) {
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  updateVariantStatuses() {
    let selectedOption1 = this.variantData.filter((v) => this.querySelector(":checked").value === v.option1);
    let controls = [...this.querySelectorAll(".product-form__controls")];

    controls.forEach((control, index) => {
      if (index !== 0) {
        let inputs = [...control.querySelectorAll('input[type="radio"], option')];
        let previousVal = controls[index - 1].querySelector(":checked").value;
        let availableOptions = selectedOption1
          .filter((v) => v.available && v["option" + index] === previousVal)
          .map((v) => v["option" + (index + 1)]);
        this.setInputAvailability(inputs, availableOptions);
      }
    });
  }

  setInputAvailability(inputs, availableOptions) {
    inputs.forEach((input) => {
      if (availableOptions.includes(input.getAttribute("value"))) {
        if (input.tagName === "OPTION") input.innerText = input.getAttribute("value");
        if (input.tagName === "INPUT") input.classList.remove("disabled");
      } else {
        if (input.tagName === "OPTION") {
          input.innerText = window.variantStrings.unavailable_with_option.replace("[value]", input.getAttribute("value"));
        }
        if (input.tagName === "INPUT") input.classList.add("disabled");
      }
    });
  }

  updatePickupAvailability() {
    let pickupEl = document.querySelector("pickup-availability");
    if (pickupEl) {
      if (this.currentVariant && this.currentVariant.available) {
        pickupEl.fetchAvailability(this.currentVariant.id);
      } else {
        pickupEl.removeAttribute("available");
        pickupEl.innerHTML = "";
      }
    }
  }

  renderProductInfo() {
    let variantId = this.currentVariant.id;
    let originalSection = this.dataset.originalSection || this.dataset.section;
    
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${originalSection}`)
      .then((res) => res.text())
      .then((htmlText) => {
        if (this.currentVariant.id === variantId) {
          let html = new DOMParser().parseFromString(htmlText, "text/html");

          if (html.querySelector(".featured-product-slideshow-section")) {
            let idx = this.dataset.index;
            let priceTarget = document.getElementById(`price-${this.dataset.section}-${idx}`);
            let priceSource = html.getElementById(`price-${originalSection}-${idx}`);
            let skuSource = html.getElementById(`Sku-${originalSection}-${idx}`);
            let skuTarget = document.getElementById(`Sku-${this.dataset.section}-${idx}`);
            let inventorySource = html.getElementById(`Inventory-${originalSection}-${idx}`);
            let inventoryTarget = document.getElementById(`Inventory-${this.dataset.section}-${idx}`);

            let colorSourceList = html.querySelectorAll(`[data-color-name-id="ColorName-${originalSection}-${idx}"]`);
            let colorTargetList = document.querySelectorAll(`[data-color-name-id="ColorName-${this.dataset.originalSection && !this.closest("quick-add-modal") ? this.dataset.originalSection : this.dataset.section}-${idx}"]`);

            if (colorSourceList?.length === colorTargetList?.length) {
              colorTargetList.forEach((el, i) => { el.innerHTML = colorSourceList[i].innerHTML; });
            }
            if (priceSource && priceTarget) priceTarget.innerHTML = priceSource.innerHTML;
            if (inventorySource && inventoryTarget) inventoryTarget.innerHTML = inventorySource.innerHTML;
            
            if (skuSource && skuTarget) {
              skuTarget.innerHTML = skuSource.innerHTML;
              skuTarget.classList.toggle("visibility-hidden", skuSource.classList.contains("visibility-hidden"));
            }

            if (priceTarget) priceTarget.classList.remove("visibility-hidden");
            if (inventoryTarget) inventoryTarget.classList.toggle("visibility-hidden", inventorySource ? inventorySource.innerText === "" : true);

          } else {
            let priceTarget = document.getElementById("price-" + this.dataset.section);
            let priceSource = html.getElementById("price-" + originalSection);
            let skuSource = html.getElementById("Sku-" + originalSection);
            let skuTarget = document.getElementById("Sku-" + this.dataset.section);
            let inventorySource = html.getElementById("Inventory-" + originalSection);
            let inventoryTarget = document.getElementById("Inventory-" + this.dataset.section);

            let colorSourceList = html.querySelectorAll(`[data-color-name-id="ColorName-${originalSection}"]`);
            let colorTargetList = document.querySelectorAll(`[data-color-name-id="ColorName-${this.dataset.originalSection && !this.closest("quick-add-modal") ? this.dataset.originalSection : this.dataset.section}"]`);

            if (colorSourceList?.length === colorTargetList?.length) {
              colorTargetList.forEach((el, i) => { el.innerHTML = colorSourceList[i].innerHTML; });
            }
            if (priceSource && priceTarget) priceTarget.innerHTML = priceSource.innerHTML;
            if (inventorySource && inventoryTarget) inventoryTarget.innerHTML = inventorySource.innerHTML;
            
            if (skuSource && skuTarget) {
              skuTarget.innerHTML = skuSource.innerHTML;
              skuTarget.classList.toggle("visibility-hidden", skuSource.classList.contains("visibility-hidden"));
            }

            if (priceTarget) priceTarget.classList.remove("visibility-hidden");
            if (inventoryTarget) inventoryTarget.classList.toggle("visibility-hidden", inventorySource ? inventorySource.innerText === "" : true);
          }

          this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);

          if (this.dataset?.variantMediaDisplay !== "show_all") {
            let mediaSource = html.querySelector(`[data-section="product-media-${originalSection}"]`);
            let mediaTarget = document.querySelector(`[data-section="product-media-${this.dataset.section}"]`);
            
            if (mediaSource && mediaTarget) {
              mediaTarget.innerHTML = mediaSource.innerHTML;
              let modal = this.closest("quick-add-modal");
              if (modal) {
                productMediaSlidersInitInContainer(modal, true);
                const quickAddSection = getQuickAddProductSection(modal);
                if (quickAddSection) initProductMediaGallery(quickAddSection);
              } else {
                let sectionIdEl = document.getElementById("shopify-section-" + this.dataset.section);
                if (sectionIdEl && typeof initProductPage === "function") initProductPage(sectionIdEl);
              }
              if (document.querySelector(".js-media-list")) {
                subSliderInit(true);
                sliderInit(true);
              }
            }
          }
        }
      });
  }

  toggleAddButton(disable = true, text, isUnavailable) {
    let formId = this.dataset.index ? `product-form-${this.dataset.section}-${this.dataset.index}` : `product-form-${this.dataset.section}`;
    let formEl = document.getElementById(formId);

    if (formEl) {
      let button = formEl.querySelector('[name="add"]');
      let buttonText = formEl.querySelector('[name="add"] > span');

      if (button) {
        if (disable) {
          button.setAttribute("disabled", "disabled");
          if (text && buttonText) buttonText.textContent = text;
        } else {
          button.removeAttribute("disabled");
          if (buttonText) buttonText.textContent = window.variantStrings.addToCart;
        }
      }
    }
  }

  setUnavailable() {
    let formId = this.dataset.index ? `product-form-${this.dataset.section}-${this.dataset.index}` : `product-form-${this.dataset.section}`;
    let formEl = document.getElementById(formId);
    let button = formEl ? formEl.querySelector('[name="add"]') : null;
    let inventory = document.getElementById(`Inventory-${this.dataset.section}${this.dataset.index ? "-" + this.dataset.index : ""}`);
    let sku = document.getElementById(`Sku-${this.dataset.section}${this.dataset.index ? "-" + this.dataset.index : ""}`);

    if (button) {
      this.toggleAddButton(true, window.variantStrings.unavailable);
      if (inventory) inventory.classList.add("visibility-hidden");
    }
    if (sku) sku.classList.add("visibility-hidden");
  }

  getVariantData() {
    if (!this.variantData) {
      this.variantData = JSON.parse(this.querySelector('[type="application/json"]').textContent);
    }
    return this.variantData;
  }
}
customElements.define("variant-selects", VariantSelects);


class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  connectedCallback() {
    this.getVariantData();
    this.bindColorSizeSelection();
    this.clearSizeSelection();
    this.updateVariantStatuses();
    this.setAwaitingSizeSelection();
  }

  onVariantChange(event) {
    if (event?.target?.dataset?.optionType === "color") return;

    if (event?.target?.dataset?.optionType === "size") {
      this.enableVariantForm();
      const { colorFieldset } = this.getColorAndSizeFieldsets();
      const selectedColor = colorFieldset?.querySelector('input[type="radio"]:checked')?.value;
      if (selectedColor) {
        this.applySizeAvailabilityForColor(selectedColor);
      }
    }

    VariantSelects.prototype.onVariantChange.call(this, event);
  }

  bindColorSizeSelection() {
    const { colorFieldset } = this.getColorAndSizeFieldsets();
    if (!colorFieldset || colorFieldset.dataset.sizeOnColorBound === "true") return;

    colorFieldset.dataset.sizeOnColorBound = "true";
    colorFieldset.addEventListener("click", (event) => {
      const input = event.target.closest('input[type="radio"]');
      if (!input) return;
      requestAnimationFrame(() => this.selectFirstAvailableSizeForColor());
    });
  }

  isColorFieldset(fieldset) {
    return (
      fieldset.classList.contains("product-form__controls--colors") ||
      Boolean(fieldset.querySelector(".variant-image-grid"))
    );
  }

  getColorAndSizeFieldsets() {
    const fieldsets = [...this.querySelectorAll("fieldset")];
    const colorFieldset = fieldsets.find((fieldset) => this.isColorFieldset(fieldset));
    const sizeFieldset = fieldsets.find((fieldset) => !this.isColorFieldset(fieldset));
    return { colorFieldset, sizeFieldset };
  }

  setVariantOptionState(input, soldOut, { keepClickable = false } = {}) {
    const label = input.closest("label");
    if (!label) return;

    label.classList.toggle("is-disabled", soldOut);
    input.classList.toggle("disabled", soldOut);

    if (keepClickable) {
      input.disabled = false;
      input.removeAttribute("aria-disabled");
      label.style.pointerEvents = "";
    } else {
      input.disabled = soldOut;
      if (soldOut) {
        input.setAttribute("aria-disabled", "true");
        label.style.pointerEvents = "none";
      } else {
        input.removeAttribute("aria-disabled");
        label.style.pointerEvents = "";
      }
    }
  }

  hasSizeSelected() {
    const { sizeFieldset } = this.getColorAndSizeFieldsets();
    return Boolean(sizeFieldset?.querySelector('input[type="radio"]:checked'));
  }

  resetSizeAvailability() {
    const { sizeFieldset } = this.getColorAndSizeFieldsets();
    if (!sizeFieldset) return;

    sizeFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      this.setVariantOptionState(input, false, { keepClickable: false });
    });
  }

  applySizeAvailabilityForColor(selectedColor) {
    const variants = this.getVariantData();
    const { sizeFieldset } = this.getColorAndSizeFieldsets();
    if (!sizeFieldset || !selectedColor) return;

    sizeFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      const variant = variants.find(
        (entry) => entry.option1 === selectedColor && entry.option2 === input.value
      );
      const available = Boolean(variant?.available);
      this.setVariantOptionState(input, !available, { keepClickable: false });
    });
  }

  updateVariantStatuses() {
    const variants = this.getVariantData();
    const { colorFieldset, sizeFieldset } = this.getColorAndSizeFieldsets();
    const selectedColor = colorFieldset?.querySelector('input[type="radio"]:checked')?.value;

    if (colorFieldset) {
      colorFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const anyAvailable = variants.some((variant) => variant.option1 === input.value && variant.available);
        this.setVariantOptionState(input, !anyAvailable, { keepClickable: true });
        if (!anyAvailable) {
          input.dataset.fullySoldOut = "true";
        } else {
          delete input.dataset.fullySoldOut;
        }
      });
    }

    if (!sizeFieldset) return;

    if (!this.hasSizeSelected()) {
      this.resetSizeAvailability();
      return;
    }

    if (selectedColor) {
      this.applySizeAvailabilityForColor(selectedColor);
    }
  }

  clearSizeSelection() {
    const { sizeFieldset } = this.getColorAndSizeFieldsets();
    if (!sizeFieldset) return;

    sizeFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
    });
  }

  setAwaitingSizeSelection() {
    this.updateOptions();
    this.currentVariant = null;

    const selectors = this.dataset.index
      ? `#product-form-${this.dataset.section}-${this.dataset.index}, #product-form-installment-${this.dataset.section}-${this.dataset.index}`
      : `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`;

    document.querySelectorAll(selectors).forEach((form) => {
      const input = form.querySelector('input[name="id"]');
      if (input) {
        input.value = "";
        input.disabled = true;
      }
    });

    const buttonLabel = window.theme?.quickAddTitle || window.variantStrings.unavailable;
    this.toggleAddButton(true, buttonLabel);
  }

  enableVariantForm() {
    const selectors = this.dataset.index
      ? `#product-form-${this.dataset.section}-${this.dataset.index}, #product-form-installment-${this.dataset.section}-${this.dataset.index}`
      : `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`;

    document.querySelectorAll(selectors).forEach((form) => {
      const input = form.querySelector('input[name="id"]');
      if (input) input.disabled = false;
    });
  }

  selectFirstAvailableSizeForColor() {
    const { colorFieldset, sizeFieldset } = this.getColorAndSizeFieldsets();
    const selectedColor = colorFieldset?.querySelector('input[type="radio"]:checked')?.value;
    if (!sizeFieldset || !selectedColor) return;

    this.updateVariantStatuses();

    sizeFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
    });

    this.applySizeAvailabilityForColor(selectedColor);

    const firstAvailable = sizeFieldset.querySelector('input[type="radio"]:not(:disabled)');
    if (!firstAvailable) {
      this.setAwaitingSizeSelection();
      return;
    }

    firstAvailable.checked = true;
    firstAvailable.dispatchEvent(new Event("change", { bubbles: true }));
  }

  setInputAvailability(inputs, availableOptions) {
    inputs.forEach((input) => {
      const keepClickable = input.dataset.optionType === "color";
      const soldOut = !availableOptions.includes(input.getAttribute("value"));
      this.setVariantOptionState(input, soldOut, { keepClickable });
    });
  }

  updateOptions() {
    let fieldsets = Array.from(this.querySelectorAll("fieldset"));
    this.options = fieldsets.map((fieldset) => {
      let checkedInput = Array.from(fieldset.querySelectorAll("input")).find((input) => input.checked);
      return checkedInput ? checkedInput.value : undefined;
    });
  }
}
customElements.define("variant-radios", VariantRadios);


class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this), {
      rootMargin: "0px 0px 400px 0px",
    });
    this.intersectionObserver.observe(this);
  }

  handleIntersection(entries, observer) {
    if (entries[0].isIntersecting) {
      observer.unobserve(this);
      this.showLoading();
      this.loadRecommendations()
        .then((html) => {
          this.updateRecommendations(html);
          this.checkSwatches();
          this.initSliders();
        })
        .catch((err) => {
          console.error("Error loading recommendations:", err);
        })
        .finally(() => {
          this.hideLoading();
        });
    }
  }

  showLoading() {
    let loader = this.querySelector(".product-recommendations__loading");
    if (loader) {
      loader.classList.add("loading");
      loader.style.display = "flex";
    }
  }

  hideLoading() {
    let loader = this.querySelector(".product-recommendations__loading");
    if (loader) loader.classList.remove("loading");
  }

  async loadRecommendations() {
    let res = await fetch(this.dataset.url);
    let text = await res.text();
    let div = document.createElement("div");
    div.innerHTML = text;
    return div;
  }

  updateRecommendations(div) {
    let recs = div.querySelector("product-recommendations");
    if (recs && recs.innerHTML.trim().length) {
      this.innerHTML = recs.innerHTML;
    }
    let emptyState = this.querySelector(".product-recommendations__empty");
    if (emptyState) emptyState.style.display = "flex";
  }

  checkSwatches() {
    document.querySelectorAll(".js-color-swatches-wrapper").forEach((wrapper) => {
      wrapper.querySelectorAll(".js-color-swatches input").forEach((input) => {
        input.addEventListener("click", this.handleSwatchClick.bind(this, wrapper, input));
      });
    });
  }

  handleSwatchClick(wrapper, input) {
    let firstMedia = wrapper.querySelector(".media--first");
    let secondMedia = wrapper.querySelector(".media--second");

    if (input.checked && firstMedia) {
      wrapper.querySelector(".js-color-swatches-link").setAttribute("href", input.dataset.variantLink);
      let btn = wrapper.querySelector('.card__add-to-cart button[name="add"]');
      let btnSpan = wrapper.querySelector('.card__add-to-cart button[name="add"] > span');
      let soldOutMsg = wrapper.querySelector('.card__add-to-cart button[name="add"] .sold-out-message');
      let idInput = wrapper.querySelector('.card__add-to-cart input[name="id"]');

      if (btn) {
        btn.setAttribute("aria-disabled", false);
        if (btnSpan) btnSpan.classList.remove("hidden");
        if (soldOutMsg) soldOutMsg.classList.add("hidden");
        idInput.value = input.dataset.variantId;
      }
      this.updateVariantImages(wrapper, firstMedia, secondMedia, input.value);
    }
  }

  async updateVariantImages(wrapper, firstMedia, secondMedia, val) {
    try {
      let res = await fetch(wrapper.dataset.url);
      if (!res.ok) throw new Error("Network response was not ok");
      let data = await res.json();
      let match = data.variants.find((v) => v.featured_media && v.options.includes(val));
      
      if (match) {
        this.replaceImages(wrapper, match, firstMedia, secondMedia, data.title);
      }
    } catch (err) {
      console.error("There was a problem with the fetch operation:", err);
    }
  }

  replaceImages(wrapper, variantData, firstMedia, secondMedia, title) {
    let newImg = this.createImageElement(variantData.featured_media, firstMedia.className, firstMedia.sizes, title);
    
    if (newImg.src !== firstMedia.src) {
      let isSamePath = false;
      if (secondMedia && new URL(secondMedia.src).pathname === new URL(newImg.src).pathname) {
        firstMedia.remove();
        secondMedia.classList.remove("media--second");
        secondMedia.classList.add("media--first");
        isSamePath = true;
      }

      if (!isSamePath) {
        this.fadeOutImage(firstMedia, () => {
          firstMedia.parentNode.replaceChild(newImg, firstMedia);
          this.fadeInImage(newImg);
          if (secondMedia) secondMedia.remove();
        });
      }
    }
  }

  fadeOutImage(img, callback) {
    img.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: "ease-in", fill: "forwards" }).onfinish = callback;
  }

  fadeInImage(img) {
    img.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: "ease-in", fill: "forwards" });
  }

  createImageElement(mediaObj, className, sizes, title) {
    let imgData = mediaObj.preview_image;
    let img = new Image(imgData.width, imgData.height);
    img.className = className;
    img.alt = mediaObj.alt || title;
    img.sizes = sizes;
    img.src = imgData.src;
    img.srcset = this.generateSrcset(imgData, [165, 360, 533, 720, 940, 1066]);
    img.loading = "lazy";
    return img;
  }

  generateSrcset(imgData, widths) {
    let url = new URL(imgData.src);
    return widths.filter((w) => w <= imgData.width).map((w) => {
      url.searchParams.set("width", w.toString());
      return url.href + ` ${w}w`;
    }).join(", ");
  }

  initSliders() {
    this.initSlider(".js-recommendation-swiper", this.setupRecommendationSlider.bind(this));
    this.initSlider(".js-complementary-swiper", this.setupComplementarySlider.bind(this));
  }

  initSlider(selector, setupFunc) {
    let el = this.querySelector(selector);
    if (el) setupFunc(el);
  }

  setupRecommendationSlider(swiperEl) {
    let nav = this.querySelector(".product-recommendations__navigation");
    let pag = this.querySelector(".product-recommendations__pagination");
    let cols = Number(swiperEl.dataset.columns);
    let bp1200 = cols;
    let bp990 = cols >= 4 ? bp1200 - 1 : bp1200;
    let bp750 = cols >= 3 ? 3 : bp1200;
    let bp576 = cols >= 2 ? 2 : bp750;

    let slidesMobile = 1;
    if (swiperEl.classList.contains("products--2-mobile")) {
      slidesMobile = cols > 1 ? 2 : 1;
    }

    let breakpoints = {
      576: { slidesPerView: bp576, slidesPerGroup: bp576, spaceBetween: 8 },
      750: { slidesPerView: bp750, slidesPerGroup: bp750, spaceBetween: 8 },
      990: { slidesPerView: bp990, slidesPerGroup: bp990, spaceBetween: 8 },
      1200: { slidesPerView: bp1200, slidesPerGroup: bp1200, spaceBetween: 24 },
      1600: { slidesPerView: cols, slidesPerGroup: cols, spaceBetween: 24 },
    };

    new Swiper(swiperEl, {
      slidesPerView: slidesMobile,
      slidesPerGroup: slidesMobile,
      spaceBetween: 8,
      mousewheel: { forceToAxis: true },
      speed: 800,
      navigation: {
        nextEl: nav.querySelector(".swiper-button-next"),
        prevEl: nav.querySelector(".swiper-button-prev"),
      },
      pagination: {
        el: pag,
        type: "custom",
        renderCustom: (swiper, current, total) => {
          let html = "";
          for (let i = 1; i <= total; i++) {
            html += `<span class="swiper-pagination-bullet ${i === current ? "swiper-pagination-bullet-active" : ""}" tabindex="0" role="button" aria-label="Go to slide ${i}"></span>`;
          }
          return html;
        },
      },
      breakpoints: breakpoints,
    });
  }

  setupComplementarySlider(swiperEl) {
    new Swiper(swiperEl, {
      spaceBetween: 8,
      mousewheel: { forceToAxis: true },
      speed: 800,
      navigation: {
        nextEl: swiperEl.querySelector(".swiper-button-next"),
        prevEl: swiperEl.querySelector(".swiper-button-prev"),
      },
      pagination: {
        el: swiperEl.querySelector(".swiper-pagination"),
        type: "fraction",
      },
    });
  }
}
customElements.define("product-recommendations", ProductRecommendations);


class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
      button: this.querySelector("button"),
      panel: this.querySelector("ul"),
    };
    this.elements.button.addEventListener("click", this.openSelector.bind(this));
    this.elements.button.addEventListener("focusout", this.closeSelector.bind(this));
    this.addEventListener("keyup", this.onContainerKeyUp.bind(this));
    this.querySelectorAll("a").forEach((a) => a.addEventListener("click", this.onItemClick.bind(this)));
  }

  hidePanel() {
    this.elements.button.setAttribute("aria-expanded", "false");
    this.elements.panel.setAttribute("hidden", true);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() === "ESCAPE") {
      this.hidePanel();
      this.elements.button.focus();
    }
  }

  onItemClick(event) {
    event.preventDefault();
    this.elements.input.value = event.currentTarget.dataset.value;
    if (this.querySelector("form")) this.querySelector("form").submit();
  }

  openSelector() {
    this.elements.button.focus();
    this.elements.panel.toggleAttribute("hidden");
    this.elements.button.setAttribute("aria-expanded", (this.elements.button.getAttribute("aria-expanded") === "false").toString());
  }

  closeSelector(event) {
    if (!this.contains(event.relatedTarget)) {
      this.hidePanel();
    }
  }
}
customElements.define("localization-form", LocalizationForm);


class MobileLocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
      button: this.querySelector("button.mobile-localization-form__button"),
      buttonBack: this.querySelector("button.mobile-localization-form__back"),
      submenu: this.querySelector(".mobile-localization-form__submenu"),
    };
    this.elements.button.addEventListener("click", this.openSelector.bind(this));
    this.elements.buttonBack.addEventListener("click", this.closeSelector.bind(this));
    this.querySelectorAll("a").forEach((a) => a.addEventListener("click", this.onItemClick.bind(this)));
  }

  onItemClick(event) {
    event.preventDefault();
    this.elements.input.value = event.currentTarget.dataset.value;
    if (this.querySelector("form")) this.querySelector("form").submit();
  }

  openSelector() {
    this.elements.button.setAttribute("aria-expanded", "true");
    this.elements.submenu.classList.add("active");
  }

  closeSelector() {
    this.elements.button.setAttribute("aria-expanded", "false");
    this.elements.submenu.classList.remove("active");
  }
}
customElements.define("mobile-localization-form", MobileLocalizationForm);

/* ============================================================================
 * 6. STANDALONE CLASSES & DOM INITIALIZATION
 * ============================================================================ */

class PasswordViewer {
  constructor() {
    document.querySelectorAll(".field--pass").forEach((field) => {
      let input = field.querySelector("input");
      let visibilityBtn = field.querySelector(".button-pass-visibility");
      let eyeClose = field.querySelector(".icon-eye-close");
      let eyeOpen = field.querySelector(".icon-eye");

      input.addEventListener("input", () => {
        visibilityBtn.style.display = input.value !== "" ? "block" : "none";
      });

      eyeClose.addEventListener("click", () => {
        input.type = "text";
        eyeClose.style.display = "none";
        eyeOpen.style.display = "block";
      });

      eyeOpen.addEventListener("click", () => {
        input.type = "password";
        eyeClose.style.display = "block";
        eyeOpen.style.display = "none";
      });
    });
  }
}


class SwipeMobile {
  constructor(element, direction = "down", threshold = 160, animTime = 400, closeCallback = () => {}) {
    this.element = element;
    this.control = this.element.querySelector(".js-mobile-swipe-control") || this.element;
    this.direction = {
      down: direction === "down",
      up: direction === "up",
      right: direction === "right",
      left: direction === "left",
    };
    this.orientation = {
      vertical: direction === "down" || direction === "up",
      horizontal: direction === "right" || direction === "left",
    };
    this.treshhold = threshold;
    this.closeAnimationTime = animTime;
    this.closeCallback = closeCallback;
    this.start = { x: 0, y: 0 };
    this.delta = { x: 0, y: 0 };
    this.closeTransform = {
      x: `translateX(${this.direction.right ? "110%" : "-110%"})`,
      y: `translateY(${this.direction.down ? "110%" : "-110%"})`,
    };

    this.startTouch = this.startTouch.bind(this);
    this.moveTouch = this.moveTouch.bind(this);
    this.endTouch = this.endTouch.bind(this);
    this.init();
  }

  close() {
    this.control.removeEventListener("touchmove", this.moveTouch);
    this.control.removeEventListener("touchend", this.endTouch);
    this.element.style.transform = this.orientation.vertical ? this.closeTransform.y : this.closeTransform.x;
    this.element.style.transition = `transform ${this.closeAnimationTime}ms`;
    
    setTimeout(() => {
      this.element.style.transform = "";
      this.element.style.transition = "";
      this.closeCallback();
    }, this.closeAnimationTime);
  }

  startTouch(event) {
    event.stopPropagation();
    this.control.addEventListener("touchmove", this.moveTouch, { passive: true });
    this.control.addEventListener("touchend", this.endTouch, { passive: true });
    this.start.x = event.touches[0].clientX;
    this.start.y = event.touches[0].clientY;
    this.element.style.transition = "transform 0ms linear 0ms";
  }

  moveTouch(event) {
    event.stopPropagation();
    this.delta.x = event.touches[0].clientX - this.start.x;
    this.delta.y = event.touches[0].clientY - this.start.y;

    let preventMove =
      (this.orientation.vertical && ((this.direction.down && this.delta.y <= 0) || (this.direction.up && this.delta.y >= 0))) ||
      (this.orientation.horizontal && ((this.direction.right && this.delta.x <= 0) || (this.direction.left && this.delta.x >= 0)));

    if (!preventMove) {
      let transformStr = this.orientation.vertical ? `translateY(${this.delta.y}px)` : `translateX(${this.delta.x}px)`;
      this.element.style.transform = transformStr;
    }
  }

  endTouch(event) {
    event.stopPropagation();
    this.element.style.transition = "";
    this.element.style.transform = "";

    let triggerClose =
      (this.orientation.vertical && ((this.direction.down && this.delta.y >= this.treshhold) || (this.direction.up && this.delta.y <= -this.treshhold))) ||
      (this.orientation.horizontal && ((this.direction.right && this.delta.x >= this.treshhold) || (this.direction.left && this.delta.x <= -this.treshhold)));

    if (triggerClose) this.close();
    this.reset();
  }

  reset() {
    this.start = { x: 0, y: 0 };
    this.delta = { x: 0, y: 0 };
  }

  init() {
    new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.contentRect.width < 576) {
          this.control.addEventListener("touchstart", this.startTouch, { passive: true });
        } else {
          this.control.removeEventListener("touchstart", this.startTouch);
          this.control.removeEventListener("touchmove", this.moveTouch);
          this.control.removeEventListener("touchend", this.endTouch);
        }
      });
    }).observe(document.documentElement);
  }
}

// -----------------------------------------------------------------------------
// Initialization IIFEs (Self-Invoking Functions)
// -----------------------------------------------------------------------------

// Initialize Mobile Swiping elements
document.querySelectorAll(".js-mobile-swipe").forEach((el) => {
  let direction;
  let callback = () => {};

  if (el.classList.contains("js-mobile-swipe-up")) direction = "up";
  else if (el.classList.contains("js-mobile-swipe-down")) direction = "down";
  else if (el.classList.contains("js-mobile-swipe-right")) direction = "right";
  else if (el.classList.contains("js-mobile-swipe-left")) direction = "left";
  else return;

  if (el.classList.contains("popup")) {
    callback = () => {
      el.classList.remove("popup-open");
      el.classList.add("popup-already-opened");
      document.body.classList.remove("popup-scroll-hidden", "popup-scroll-hidden-mobile");
    };
  }

  if (el.classList.contains("banner-products__wrapper")) {
    callback = () => {
      el.closest(".banner-products__inner").classList.remove("active");
      document.body.classList.remove("overflow-hidden-products");
    };
  }

  if (el.classList.contains("quick-add-modal__content")) {
    callback = () => {
      el.closest("quick-add-modal").hide(true);
    };
  }

  if (el.classList.contains("drawer__inner") && el.closest("cart-drawer")) {
    callback = () => {
      el.closest("cart-drawer").close();
    };
  }

  if (el.id === "menu-drawer" && el.closest("header-drawer")) {
    callback = () => {
      el.closest("header-drawer").closeMenuDrawer(true);
    };
  }

  if ((el.classList.contains("menu-drawer__submenu") && el.id.startsWith("childlink-")) ||
      (el.classList.contains("menu-drawer__submenu") && el.id.startsWith("link-"))) {
    callback = () => {
      el.closest("menu-drawer").closeSubmenu(el.closest("menu-drawer").mainDetailsToggle);
    };
  }

  if (el.classList.contains("mobile-localization-form__submenu") && el.closest("mobile-localization-form")) {
    callback = () => {
      el.closest("mobile-localization-form").closeSelector();
    };
  }

  new SwipeMobile(el, direction, 160, 400, callback);
});